import { Sprite, Texture } from 'pixi.js';
import { POOL_CAPACITY } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import { NODE_SLUG } from '../assets/registry';
import type { StackRegistry } from '../assets/StackRegistry';
import { WORLD_TO_R2, placeSprite } from '../scale';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

const NODE_CHARGE_SPLIT = 0.5;
const STATE_DISABLED = 'disabled';
const STATE_CHARGE_HIGH = 'charge1';
const STATE_CHARGE_LOW = 'charge0';

const TETHER_COLOR = 0x50e8ff;
const TETHER_WIDTH = 2;
const TETHER_BASE_ALPHA = 0.5;
const TETHER_ALPHA_SWING = 0.2;
const TETHER_PHASE_RATE = 0.4;
const MIN_TETHER_LENGTH = 1;

/**
 * The three PRISM NODES. The tether ring they draw when the formation is live
 * is a separate pool — see syncTether — of pooled `Sprite(Texture.WHITE)`
 * (one per edge of the triangle, fixed at POOL_CAPACITY.nodes), rotated and
 * sized between the two node endpoints instead of stroked into the shared
 * `beams` Graphics every frame.
 */
export class NodesFamily implements EntityFamily {
  private stacks: StackRegistry | null = null;
  private readonly sprites: LayerStackSprite[] = [];
  private readonly tetherSprites: Sprite[] = [];

  init(deps: FamilyDeps): void {
    this.stacks = deps.stacks;
    for (let i = 0; i < POOL_CAPACITY.nodes; i += 1) this.sprites.push(deps.stacks.acquire(NODE_SLUG, deps.layer));
    for (let i = 0; i < POOL_CAPACITY.nodes; i += 1) {
      const tether = new Sprite(Texture.WHITE);
      tether.anchor.set(0.5);
      tether.roundPixels = true;
      tether.visible = false;
      tether.tint = TETHER_COLOR;
      deps.beamLayer.addChild(tether);
      this.tetherSprites.push(tether);
    }
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    const stacks = this.stacks;
    if (!stacks) return;
    for (let i = 0; i < this.sprites.length; i += 1) {
      const sprite = this.sprites[i]!;
      sprite.visible = world.player.alive;
      if (!sprite.visible) continue;
      const state = world.nodes.disabledTicks[i]! > 0
        ? STATE_DISABLED
        : world.nodes.charge[i]! >= NODE_CHARGE_SPLIT ? STATE_CHARGE_HIGH : STATE_CHARGE_LOW;
      stacks.applyState(NODE_SLUG, sprite, state);
      placeSprite(sprite, world.nodes.x[i]!, world.nodes.y[i]!);
    }
  }

  syncTether(world: GameWorld, _frame: FrameContext): void {
    const active = world.nodes.tetherActiveTicks > 0;
    for (let i = 0; i < POOL_CAPACITY.nodes; i += 1) {
      const tether = this.tetherSprites[i]!;
      if (!active) {
        tether.visible = false;
        continue;
      }
      const next = (i + 1) % POOL_CAPACITY.nodes;
      const x1 = world.nodes.x[i]! * WORLD_TO_R2;
      const y1 = world.nodes.y[i]! * WORLD_TO_R2;
      const x2 = world.nodes.x[next]! * WORLD_TO_R2;
      const y2 = world.nodes.y[next]! * WORLD_TO_R2;
      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.max(MIN_TETHER_LENGTH, Math.hypot(dx, dy));
      tether.visible = true;
      tether.alpha = TETHER_BASE_ALPHA + Math.sin(world.simTick * TETHER_PHASE_RATE + i) * TETHER_ALPHA_SWING;
      tether.rotation = Math.atan2(dy, dx);
      tether.setSize(length, TETHER_WIDTH * WORLD_TO_R2);
      tether.position.set(Math.round((x1 + x2) / 2), Math.round((y1 + y2) / 2));
    }
  }

  dispose(): void {
    this.sprites.length = 0;
    this.tetherSprites.length = 0;
    this.stacks = null;
  }
}
